// SAP-8323: Set Open Enrollment, a plan's fixed renewal date ("MM-DD" days,
// recurring yearly): every member renews on the Enrollment Start Date.

export const OPEN_ENROLLMENT_FIELDS = {
  // Note: Screen-only. The Set Open Enrollment switch isn't saved; it shows
  // the row below Plan Attributes and is on when the plan has any of the
  // row's fields. SaveDetails drops it from the save payload.
  enabled: 'is_open_enrollment',
  startDay: 'enrollment_start_day',
  endDay: 'enrollment_end_day',
  // Saved as true, false, or null (the member type's default).
  autoRenew: 'is_auto_renew',
};

export const SET_OPEN_ENROLLMENT_LABEL = 'Set Open Enrollment';

// Annual prices bill on the start-date anniversary, so they can't have a
// fixed renewal date.
const ANNUAL_BILLING_FREQUENCY = 'Annual';

// Whether a saved plan has any Set Open Enrollment field.
export const hasOpenEnrollmentSettings = (plan = {}) =>
  (plan[OPEN_ENROLLMENT_FIELDS.autoRenew] !== null &&
    plan[OPEN_ENROLLMENT_FIELDS.autoRenew] !== undefined) ||
  Boolean(plan[OPEN_ENROLLMENT_FIELDS.startDay]) ||
  Boolean(plan[OPEN_ENROLLMENT_FIELDS.endDay]);

// The switch as last set on the screen, else from the plan's fields.
export const isOpenEnrollmentOn = (productInfo = {}) =>
  productInfo[OPEN_ENROLLMENT_FIELDS.enabled] ??
  hasOpenEnrollmentSettings(productInfo);

export const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

// A leap year, so February 29 can be chosen; it is used as February 28 in
// non-leap years.
const REFERENCE_YEAR = 2028;

const PLAN_DAY_PATTERN = /^(\d{2})-(\d{2})$/;

const pad = (value) => String(value).padStart(2, '0');

export const daysInMonth = (month) =>
  new Date(Date.UTC(REFERENCE_YEAR, month, 0)).getUTCDate();

export const parsePlanDay = (planDay) => {
  const match = PLAN_DAY_PATTERN.exec(planDay || '');
  if (!match) return null;

  const month = Number(match[1]);
  const day = Number(match[2]);
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth(month)) {
    return null;
  }
  return { month, day };
};

export const formatPlanDay = (month, day) =>
  month && day ? `${pad(month)}-${pad(day)}` : '';

export const formatPlanDayLabel = (planDay) => {
  const parsed = parsePlanDay(planDay);
  return parsed ? `${MONTHS[parsed.month - 1]} ${parsed.day}` : '';
};

// The date pickers need a year: plan days are shown in REFERENCE_YEAR, a
// leap year, so February 29 can be picked. Only the month and day are kept.
export const PLAN_DAY_MIN_DATE = new Date(REFERENCE_YEAR, 0, 1);
export const PLAN_DAY_MAX_DATE = new Date(REFERENCE_YEAR, 11, 31, 23, 59, 59);

export const planDayToDate = (planDay) => {
  const parsed = parsePlanDay(planDay);
  return parsed ? new Date(REFERENCE_YEAR, parsed.month - 1, parsed.day) : null;
};

export const dateToPlanDay = (date) =>
  date instanceof Date && !Number.isNaN(date.getTime())
    ? formatPlanDay(date.getMonth() + 1, date.getDate())
    : '';

// The day after a plan day, in a leap year: 02-28 -> 02-29, 12-31 -> 01-01.
export const getDayAfter = (planDay) => {
  const parsed = parsePlanDay(planDay);
  if (!parsed) return '';

  const next = new Date(Date.UTC(REFERENCE_YEAR, parsed.month - 1, parsed.day));
  next.setUTCDate(next.getUTCDate() + 1);
  return formatPlanDay(next.getUTCMonth() + 1, next.getUTCDate());
};

// The Auto Renew value shown on the plan details screen.
export const formatAutoRenewLabel = (autoRenew, isOnetimePlan) => {
  if (autoRenew === null || autoRenew === undefined) return 'Not set';
  return autoRenew && !isOnetimePlan ? 'Yes' : 'No';
};

// The value saved for a Set Open Enrollment field: an empty day is saved as
// null, and Auto Renew as true, false or null (not set).
export const toSavedOpenEnrollmentValue = (name, value) => {
  if (name !== OPEN_ENROLLMENT_FIELDS.autoRenew) return value || null;
  return value === null || value === undefined ? null : Boolean(value);
};

// Mirrors the backend's plan save check, so the screen can show it first.
// billingFrequencies are the plan's prices' frequencies. Returns an error
// message, or '' when the fields are valid.
export const getOpenEnrollmentError = (
  productInfo = {},
  billingFrequencies = [],
) => {
  if (!isOpenEnrollmentOn(productInfo)) return '';

  const startDay = productInfo[OPEN_ENROLLMENT_FIELDS.startDay] || '';
  const endDay = productInfo[OPEN_ENROLLMENT_FIELDS.endDay] || '';

  if (Boolean(startDay) !== Boolean(endDay)) {
    return 'Enter both Enrollment Start Date and Enrollment End Date, or neither';
  }
  if (startDay && (!parsePlanDay(startDay) || !parsePlanDay(endDay))) {
    return 'Enter a valid month and day';
  }
  // Members are billed monthly on the renewal day, which must exist in
  // every month.
  if (startDay && parsePlanDay(startDay).day > 28) {
    return 'Enrollment Start Date must be on day 1 to 28 of the month';
  }
  if (startDay && startDay !== getDayAfter(endDay)) {
    return `Enrollment Start Date must be the day after Enrollment End Date (${formatPlanDayLabel(
      getDayAfter(endDay),
    )})`;
  }
  if (startDay && billingFrequencies.includes(ANNUAL_BILLING_FREQUENCY)) {
    return "Enrollment dates can't be used on a plan with an Annual price";
  }
  if (
    productInfo[OPEN_ENROLLMENT_FIELDS.autoRenew] &&
    productInfo.is_onetime_plan
  ) {
    return "Auto Renew can't be used on one-time plans";
  }
  return '';
};
